import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardActions, Grid, Typography } from '@mui/material';
import {
  ViewConneqtPayload,
  ConneqtStatus,
} from '../../../state/reduxRepositories/conneqt/conneqt.interfaces';
import { getPlayerDetails } from '../conneqt.helper';
import { BadgeStatus } from '../conneqt.status';
import { getInitials } from '../../../helpers/initialsExtractor';
import { di } from '../../../state/inversify.config';
import { LoginDomainService } from '../../login/loginDomainService';
import { ConneqtPageQueryService } from '../conneqt.queryParam.service';
import { SentConneqtDomain } from './sentConneqt.domain';
import { EmailWithIcon } from '../../../components/emailWithIcon/emailWithIcon';
import { dateTime } from '../../../time/datetime';
import { Avatar } from '../../../components/avatar/avatar';
import { Button } from '../../../components/button/button';

interface SentRequestsProps {
  pageQueryParam: ConneqtPageQueryService;
}
export function SentRequests({ pageQueryParam }: SentRequestsProps) {
  const { t } = useTranslation();

  const { selectedConneqtId } = pageQueryParam.useSelectMergedQuery();

  const loginDomainService = di.get<LoginDomainService>(LoginDomainService);
  const isNetworkConnected = loginDomainService.useNetworkSelector();

  const sentConneqtDomain = di.get<SentConneqtDomain>(SentConneqtDomain);
  const sentConneqts = sentConneqtDomain.useSentConneqts();

  const deleteConneqt = (conneqtId: string) => {
    sentConneqtDomain.dispatchDeleteConneqt(conneqtId);
  };

  const sendEmail = (conneqt: ViewConneqtPayload) => {
    sentConneqtDomain.dispatchInviteSentStatus(conneqt.id);
  };

  const allowConneqtDelete = (status: ConneqtStatus) => {
    return (
      status === ConneqtStatus.Pending ||
      status === ConneqtStatus.INVITE_SENT ||
      status === ConneqtStatus.AccountNotFound
    );
  };

  useEffect(() => {
    if (!selectedConneqtId) return;

    const element = document.getElementById(selectedConneqtId);
    if (!element) return;

    // 👇 Will scroll smoothly to the top of the next section
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [sentConneqts, selectedConneqtId]);

  return (
    <Grid container spacing={2}>
      {sentConneqts?.map((conneqt: ViewConneqtPayload) => (
        <Grid item xs={12} sm={12} md={8} lg={4} key={conneqt.id}>
          <Card sx={{ maxWidth: 370, borderRadius: 4 }}>
            <CardHeader
              avatar={
                <Avatar
                  title={getInitials(
                    getPlayerDetails(conneqt.requestPlayerClues)?.name,
                  )}
                />
              }
              action={
                <BadgeStatus
                  testId={`status-${getPlayerDetails(
                    conneqt.requestPlayerClues,
                  )?.name.replace(/\s/g, '')}`}
                  status={conneqt.status}
                />
              }
              title={getPlayerDetails(conneqt.requestPlayerClues)?.name}
              titleTypographyProps={{ variant: 'subtitle2' }}
              subheaderTypographyProps={{ variant: 'subtitle2' }}
              subheader={
                <Typography variant="subtitle2">
                  {dateTime.format(
                    getPlayerDetails(conneqt.requestPlayerClues)?.dob,
                    t('common.datetime.FORMAT_SHORT_DATE'),
                  )}
                </Typography>
              }
            />
            <Typography variant="subtitle2" sx={{ ml: 3 }}>
              <EmailWithIcon email={conneqt.responseEmail} />
            </Typography>

            <CardActions>
              {conneqt.status === ConneqtStatus.AccountNotFound &&
                isNetworkConnected && (
                  <Button
                    color="secondary"
                    type="submit"
                    variant="contained"
                    fullWidth
                    onClick={() => sendEmail(conneqt)}
                    label={t('SEND_INVITE')}
                  />
                )}
              {allowConneqtDelete(conneqt.status) && isNetworkConnected ? (
                <Button
                  color="secondary"
                  type="submit"
                  variant="contained"
                  fullWidth
                  onClick={() => deleteConneqt(conneqt.id)}
                  label={t('CANCEL_INVITE')}
                />
              ) : null}
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
